'use client'

import React, { type ReactNode, useCallback, useMemo, useState } from 'react'

import { ChevronDown } from 'lucide-react'
import { twMerge } from 'tailwind-merge'

import { useSectionHighlight } from '../RecipientDetail/context'

export interface SectionCardProps {
  /** HTML id for anchor linking */
  id?: string
  /** Section title displayed in header */
  title: ReactNode
  /** Optional subtitle or description */
  subtitle?: string
  /** Section content */
  children: ReactNode
  /** Make section span full width (2 columns on lg) */
  fullWidth?: boolean
  /** Allow section to be collapsed */
  collapsible?: boolean
  /** Initial collapsed state */
  defaultCollapsed?: boolean
  /** Additional CSS classes */
  className?: string
  /** Header action slot (e.g., download button) */
  headerAction?: ReactNode
  /** Animation delay for staggered reveal (in ms) */
  animationDelay?: number
  /** Disable the default mount animation */
  disableMountAnimation?: boolean
  /** Mouse enter handler on the root element */
  onMouseEnter?: React.MouseEventHandler<HTMLDivElement>
}

function stopPropagation(e: React.MouseEvent) {
  e.stopPropagation()
}

// Highlight styling classes (matches DocumentsSection pattern)
const HIGHLIGHT_CLASSES =
  'ring-2 ring-accent-400 shadow-[0_0_0_4px_rgba(99,102,241,0.15)] dark:ring-accent-500'
const HIGHLIGHT_TRANSITION_IN = 'transition-all duration-200 ease-in'
const HIGHLIGHT_TRANSITION_OUT = 'transition-all duration-1000 ease-out'

/**
 * SectionCard
 *
 * Collapsible section wrapper for dashboard cards.
 * Provides consistent styling and animation across all dashboard sections.
 *
 * Features:
 * - Staggered reveal animation on mount
 * - Optional collapse/expand
 * - Header action slot for buttons/links
 * - Full-width mode for 2-column spanning
 */
export const SectionCard: React.FC<SectionCardProps> = ({
  id,
  title,
  subtitle,
  children,
  fullWidth = false,
  collapsible = false,
  defaultCollapsed = false,
  className,
  headerAction,
  animationDelay = 0,
  disableMountAnimation = false,
  onMouseEnter,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed)

  const handleCollapseToggle = useCallback(
    () => setIsCollapsed((prev) => !prev),
    [],
  )

  // Check if this section is highlighted (from minimap navigation)
  // Safe to call - returns false if not in RecipientDetail context
  const isHighlighted = useSectionHighlight(id ?? '')

  // Memoize style object to avoid recreation (rerender-memo-with-default-value)
  const animationStyle = useMemo(
    () => ({ animationDelay: `${animationDelay}ms` }),
    [animationDelay],
  )

  return (
    <div
      id={id}
      className={twMerge(
        'flex flex-col',
        'rounded-lg border border-primary-200 bg-white dark:border-primary-800 dark:bg-primary-900',
        'shadow-sm hover:shadow-md',
        !disableMountAnimation &&
          'animate-[fadeSlideIn_300ms_ease-out_backwards]',
        fullWidth && 'md:col-span-2',
        // Highlight styling when section is scrolled to
        isHighlighted ? HIGHLIGHT_CLASSES : '',
        isHighlighted ? HIGHLIGHT_TRANSITION_IN : HIGHLIGHT_TRANSITION_OUT,
        className,
      )}
      style={animationStyle}
      onMouseEnter={onMouseEnter}
    >
      {/* Header */}
      <div
        className={twMerge(
          'flex items-center justify-between border-b border-primary-100 px-3 py-2 sm:px-4 sm:py-2.5 dark:border-primary-800',
          collapsible && 'cursor-pointer select-none',
        )}
        onClick={collapsible ? handleCollapseToggle : undefined}
      >
        <div className="min-w-0 flex-1">
          <h3 className="text-xs font-bold uppercase tracking-wide text-primary-900 dark:text-primary-100">
            {title}
          </h3>
          {subtitle && (
            <p className="mt-0.5 text-xs text-primary-500 dark:text-primary-400">
              {subtitle}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2">
          {headerAction && <div onClick={stopPropagation}>{headerAction}</div>}
          {collapsible && (
            <ChevronDown
              className={twMerge(
                'h-4 w-4 text-primary-400 transition-transform duration-200 dark:text-primary-500',
                isCollapsed && '-rotate-90',
              )}
            />
          )}
        </div>
      </div>

      {/* Content */}
      <div
        className={twMerge(
          'flex-1 overflow-hidden transition-all duration-200',
          isCollapsed ? 'max-h-0' : 'max-h-[2000px]',
        )}
      >
        <div className="flex h-full flex-col p-3 sm:p-4">{children}</div>
      </div>

      {/* Animation keyframes */}
      <style jsx>{`
        @keyframes fadeSlideIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  )
}

/**
 * SectionSkeleton
 *
 * Loading placeholder for SectionCard content.
 */
export interface SectionSkeletonProps {
  /** Skeleton height (default 200px) */
  height?: number
  /** Number of animated bars to show */
  bars?: number
}

export const SectionSkeleton: React.FC<SectionSkeletonProps> = ({
  height = 200,
  bars = 3,
}) => {
  return (
    <div className="animate-pulse" style={{ height }}>
      <div className="flex flex-col gap-3">
        {Array.from({ length: bars }).map((_, i) => (
          <div key={i} className="flex items-center gap-3">
            <div
              className="h-4 rounded bg-primary-200 dark:bg-primary-700"
              style={{ width: `${70 - i * 15}%` }}
            />
            <div className="h-4 w-16 rounded bg-primary-100 dark:bg-primary-800" />
          </div>
        ))}
      </div>
    </div>
  )
}
